import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Label from "./LabelF";
import { getAtributo } from "../../../context/FuncionesContext";
import {
  crearBarcodeDetector,
  detectarCodigoEnImagen,
  startCameraScanner,
  waitForVideoElement,
} from "../../../../../helpers/scanner";
import { entregarValorAInputDestino } from "../../../../../helpers/inputDestino";

const isMobileDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera || "";

  return (
    /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(userAgent) ||
    window.matchMedia?.("(max-width: 900px)").matches
  );
};

const InputCam = (props) => {
  const { setValor, cab, data } = props;
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const buttonRef = useRef(null);
  const videoRef = useRef(null);
  const scannerInputRef = useRef(null);
  const scannerStreamRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scannerActiveRef = useRef(false);
  const scannerLockRef = useRef(false);

  const inputDestino =
    getAtributo("input_destino", data, cab) ??
    getAtributo("destino", data, cab);
  const visibleEnPc =
    (getAtributo("visible_en_pc", data, cab) ??
      getAtributo("visible_pc", data, cab) ??
      "n") === "s";
  const formatos = getAtributo("formatos", data, cab);

  const mobile = isMobileDevice();
  const supportsBarcodeDetector =
    typeof window !== "undefined" && "BarcodeDetector" in window;
  const supportsCamera =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  useEffect(() => {
    return () => {
      if (scannerControlsRef.current?.stop) {
        scannerControlsRef.current.stop();
      }

      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const stopScanner = () => {
    scannerActiveRef.current = false;

    if (scannerControlsRef.current?.stop) {
      scannerControlsRef.current.stop();
      scannerControlsRef.current = null;
    }

    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    scannerLockRef.current = false;
    setIsScannerOpen(false);
    setIsProcessingScan(false);
  };

  const applyScannedValue = (rawValue) => {
    const value = String(rawValue ?? "").trim();

    if (!value || !inputDestino) {
      return;
    }

    setScannerError("");
    const entregadoAlInput = entregarValorAInputDestino({
      origen: buttonRef.current,
      destino: inputDestino,
      valor: value,
    });

    if (entregadoAlInput) return;

    setValor(value, inputDestino);
  };

  const handleDetectedCode = (rawValue) => {
    if (!rawValue || scannerLockRef.current) return;

    scannerLockRef.current = true;
    applyScannedValue(rawValue);
    stopScanner();
  };

  const openScanner = async () => {
    setScannerError("");

    if (!inputDestino || (!mobile && !visibleEnPc)) {
      return;
    }

    if (!supportsCamera) {
      if (scannerInputRef.current) {
        scannerInputRef.current.click();
      }
      return;
    }

    try {
      scannerActiveRef.current = true;
      setIsScannerOpen(true);
      setIsProcessingScan(true);

      const video = await waitForVideoElement(videoRef);
      const controls = await startCameraScanner("multi-format", {
        video,
        onDetected: handleDetectedCode,
        formatos,
      });

      if (!scannerActiveRef.current || scannerLockRef.current) {
        controls.stop();
        return;
      }

      scannerControlsRef.current = controls;
      scannerStreamRef.current = controls.getStream?.() ?? null;
      setIsProcessingScan(false);
    } catch (error) {
      setScannerError("No se pudo iniciar el lector de codigos.");
      stopScanner();
    }
  };

  const handleScannerFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    try {
      const detector = await crearBarcodeDetector(
        supportsBarcodeDetector,
        formatos,
      );
      const imageBitmap = await createImageBitmap(file);
      const code = await detectarCodigoEnImagen(imageBitmap, detector, formatos);

      if (!code) {
        setScannerError("No se detecto ningun codigo compatible en la imagen.");
        return;
      }

      applyScannedValue(code);
    } catch (error) {
      setScannerError("No se pudo procesar la imagen capturada.");
    }
  };

  if (!inputDestino || (!mobile && !visibleEnPc)) {
    return null;
  }

  return (
    <>
      {cab.nombre ? (
        <Label
          label={cab.nombre}
          opcionales_null={cab.opcionales_null}
          permite_null={cab.permite_null}
        />
      ) : null}
      <Button
        ref={buttonRef}
        variant="contained"
        onClick={openScanner}
        className={cab.input_class_caja}
        style={{
          width: cab.input_size || cab.width || "100%",
          minWidth: cab.input_size || cab.min_width,
          marginBottom: cab.margin_bottom_abm,
        }}
        disabled={
          data[cab.id_a + "_disabled"]
            ? data[cab.id_a + "_disabled"] === "s"
            : cab.disabled === "s"
        }
      >
        {cab.label || cab.placeholder || "Escanear con camara"}
      </Button>
      <input
        ref={scannerInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleScannerFile}
      />
      {scannerError ? (
        <div
          style={{
            marginTop: "6px",
            color: "#b42318",
            fontSize: "0.8rem",
          }}
        >
          {scannerError}
        </div>
      ) : null}
      {isScannerOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            zIndex: 1600,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
            gap: "12px",
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "16px",
              background: "#000",
            }}
          />
          <div
            style={{
              color: "#fff",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            {isProcessingScan
              ? "Abriendo camara..."
              : "Apunta la camara al codigo"}
          </div>
          <Button
            variant="contained"
            onClick={stopScanner}
            style={{
              background: "#fff",
              color: "#111",
            }}
          >
            Cancelar
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default InputCam;
